import styled, { css } from 'styled-components';

/**
 * Componentes estilizados comuns reutilizáveis
 * IMPORTANTE: CSS mixins usam tema estático, styled components usam props.theme
 */

// ==================== MIXINS ====================

/**
 * Mixin para flexbox centralizado
 */
export const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Mixin para flexbox com espaço entre elementos
 */
export const flexBetween = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/**
 * Mixin para flexbox em coluna
 */
export const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

/**
 * Mixin para truncar texto com ellipsis
 */
export const textEllipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Mixin para efeito de hover em cards
 */
export const cardHoverEffect = css`
  transition: transform ${props => props.theme.transitions.normal}, box-shadow ${props => props.theme.transitions.normal};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

// ==================== CONTAINERS ====================

/**
 * Container principal para páginas
 */
export const PageContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.lg};
`;

/**
 * Container de página com fundo
 */
export const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.bgSecondary};
  padding: ${props => props.theme.spacing.lg};
`;

/**
 * Container centralizado para formulários/cards de autenticação
 */
export const CenteredContainer = styled.div`
  ${flexCenter}
  min-height: 100vh;
  padding: ${props => props.theme.spacing.lg};
`;

// ==================== CARDS ====================

/**
 * Card base estilizado
 */
export const StyledCard = styled.div`
  background: ${props => props.theme.colors.bgPrimary};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.sm};
  padding: ${props => props.theme.spacing.lg};
`;

/**
 * Card com efeito de hover
 */
export const InteractiveCard = styled(StyledCard)`
  ${cardHoverEffect}
  cursor: pointer;
`;

// ==================== HEADERS/TÍTULOS ====================

/**
 * Header de página com título
 */
export const PageHeader = styled.div`
  ${flexBetween}
  margin-bottom: ${props => props.theme.spacing.lg};
`;

/**
 * Título de página
 */
export const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize.xxl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

/**
 * Subtítulo ou descrição de página
 */
export const PageSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: ${props => props.theme.spacing.xs} 0 0 0;
`;

// ==================== GRIDS ====================

/**
 * Grid responsivo para estatísticas/cards
 */
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${props => props.theme.spacing.md};
`;

/**
 * Grid de duas colunas
 */
export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.md};

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

// ==================== FORMULÁRIOS ====================

/**
 * Container de formulário
 */
export const FormContainer = styled.div`
  max-width: 800px;
`;

/**
 * Row de formulário
 */
export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
`;

/**
 * Seção de formulário com título
 */
export const FormSection = styled.section`
  margin-bottom: ${props => props.theme.spacing.xl};

  & > h3 {
    font-size: ${props => props.theme.typography.fontSize.lg};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    margin-bottom: ${props => props.theme.spacing.md};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

// ==================== LOADING/EMPTY STATES ====================

/**
 * Container para loading centralizado
 */
export const LoadingContainer = styled.div`
  ${flexCenter}
  min-height: 200px;
  padding: ${props => props.theme.spacing.xxl};
`;

/**
 * Container para estados vazios
 */
export const EmptyStateContainer = styled.div`
  ${flexCenter}
  ${flexColumn}
  padding: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.textSecondary};
  text-align: center;
`;

// ==================== MISC ====================

/**
 * Divisor horizontal
 */
export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.colors.borderSecondary};
  margin: ${props => props.theme.spacing.lg} 0;
`;

/**
 * Badge/Tag customizado
 */
export const Badge = styled.span<{ variant?: 'success' | 'warning' | 'error' | 'info' }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  border-radius: ${props => props.theme.borderRadius.sm};
  
  ${({ variant = 'info', theme }) => {
    const colors = {
      success: { bg: '#f6ffed', color: theme.colors.success },
      warning: { bg: '#fffbe6', color: theme.colors.warning },
      error: { bg: '#fff2f0', color: theme.colors.error },
      info: { bg: '#e6f7ff', color: theme.colors.primary },
    };
    return css`
      background-color: ${colors[variant].bg};
      color: ${colors[variant].color};
    `;
  }}
`;

/**
 * Texto com ícone alinhado
 */
export const IconText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;
